import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { setCulture } from '@syncfusion/ej2-base';
import { TranslocoService } from '@jsverse/transloco';
import { filter, switchMap, tap } from 'rxjs';
import { ApplicationStateService } from './shared/services/application-state.service';
import { NavigationStart, Router, RouterOutlet } from '@angular/router';
import { ToastService } from './shared/services/toast.service';
import { CurrentUserService } from './shared/services/current-user.service';
import { NewVersionBannerComponent } from './shared/components/new-version-banner/new-version-banner.component';

@Component({
  selector: 'goatsports-root',
  imports: [RouterOutlet, NewVersionBannerComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly translocoService = inject(TranslocoService);
  private readonly applicationStateService = inject(ApplicationStateService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);

  private readonly onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.currentUserService.reportTimezoneIfChanged();
    }
  };

  ngOnInit() {
    this.currentUserService.reportTimezoneIfChanged();
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  ngOnDestroy() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  routeChanges = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationStart),
      tap(() => {
        this.toastService.hideAllToasts();
      }),
    ),
  );

  languageChanges = toSignal(
    this.translocoService.langChanges$.pipe(
      switchMap((language) =>
        this.translocoService.load(language).pipe(
          tap(() => {
            this.applicationStateService.languageChanged = language;
            setCulture(language);
          }),
        ),
      ),
    ),
  );
}
